import { ethers } from 'ethers';
import { v4 as uuid } from 'uuid';
import { THUNK_PREFIX } from 'store/enum';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { TxnStatus, TxnType } from 'store/transactions/types';
import TxnError from 'components/toasts/TxnError';
import parseTransactionError from 'utils/parseTransactionError';
import TxnPendingApprovals from 'components/toasts/TxnPendingApprovals';
import { truncate } from 'utils/address';

export const registerBonusToken = createAsyncThunk(
  THUNK_PREFIX.FUND_ALUDEL,
  async ({
    web3React,
    updateTx,
    toast,
    modal,
    logger,
    aludelAddress,
    bonusToken,
    rewardProgramDataMethod,
    aludelAbi
  }: any) => {
    // create UUID for tracking these tx
    let txnId = uuid();

    const description = `Register bonus token
      ${bonusToken.tokenSymbol.toUpperCase()} in Aludel ${truncate(
      aludelAddress
    )}`;

    updateTx({
      id: txnId,
      type: TxnType.registerBonusToken,
      status: TxnStatus.Initiated,
      description
    });

    try {
      const { account, chainId, library } = web3React;

      const aludel = new ethers.Contract(
        aludelAddress,
        aludelAbi,
        library.getSigner()
      );

      const aludelData = await aludel[rewardProgramDataMethod].call();
      const stakingTokenAddress = aludelData[0].toLowerCase();
      const rewardTokenAddress = aludelData[1].toLowerCase();

      if (
        bonusToken.address.toLowerCase() === stakingTokenAddress.toLowerCase()
      ) {
        throw new Error(`Bonus token cannot be the same as staking token`);
      }

      if (bonusToken.address.toLowerCase() === rewardTokenAddress.toLowerCase())
        throw new Error(`Bonus token cannot be the same as reward token`);

      const numBonusTokens = (await aludel.getBonusTokenSetLength()).toNumber();
      const allBonusTokens = [];
      for (let i = 0; i < numBonusTokens; i++)
        allBonusTokens.push(
          (await aludel.getBonusTokenAtIndex(i)).toLowerCase()
        );
      if (allBonusTokens.includes(bonusToken.address.toLowerCase())) {
        const errorMessage = 'Bonus token already registered, skipping.';
        updateTx({
          id: txnId,
          status: TxnStatus.Mined,
          error: errorMessage
        });

        return 'registered';
      }

      updateTx({
        id: txnId,
        status: TxnStatus.PendingApproval,
        account,
        chainId
      });

      toast.closeAll();
      toast({
        duration: null,
        isClosable: true,
        position: 'bottom-right',
        render: () => (
          <TxnPendingApprovals description='Confirm bonus token registration transaction' />
        )
      });

      const registerTx = await aludel.registerBonusToken(bonusToken.address);

      updateTx({
        id: txnId,
        status: TxnStatus.PendingOnChain,
        hash: registerTx.hash
      });

      await registerTx.wait(1);

      updateTx({
        id: txnId,
        status: TxnStatus.Mined,
        hash: registerTx.hash
      });
      return bonusToken;
    } catch (error) {
      const errorMessage = parseTransactionError(error);

      toast.closeAll();
      toast({
        duration: null,
        isClosable: true,
        position: 'bottom-right',
        // @ts-ignore
        render: ({ onClose }) => (
          <TxnError
            onClose={onClose}
            modal={modal}
            errorMessage={errorMessage}
            error={error}
          />
        )
      });
      updateTx({
        id: txnId,
        status: TxnStatus.Failed,
        error: errorMessage
      });

      logger.push(error);

      // trigger redux toolkit's rejected.match hook
      throw error;
    }
  }
);
